import { Tooltip } from "@idui/react-popover";
import { Badge } from "@mui/material";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { deepPurpleMui, greyMui, rydeColors, ScreenDepth } from "@ryde/theme";

export const LeftBarNavigationStyled = styled.ul.attrs<IStyledAttributes>({
  "data-e2e": "left-bar-navigation-ul",
})`
  padding-inline-start: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  li {
    list-style: none;
  }
`;

export const StyledBadgeAvatar = styled(Badge)`
  & .MuiBadge-badge {
    background-color: ${rydeColors.leftMenuHighlight};
    color: ${rydeColors.leftMenuHighlight};
    box-shadow: 0 0 0 2px ${greyMui._700};
    &::after {
      position: absolute;
      top: 0;
      inset-inline-start: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      animation: ripple 1.6s infinite ease-in-out;
      border: 1px solid currentColor;
      content: "";
    }
  }
  @keyframes ripple {
    0% {
      transform: scale(0.8);
      opacity: 1;
    }
    100% {
      transform: scale(1.4);
      opacity: 0;
    }
  }
`;

export const AdminNavIconWrapper = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  color: ${props =>
    props.$active ? rydeColors.leftMenuHighlight : rydeColors.white};
  .MuiSvgIcon-root {
    font-size: 22px;
    color: inherit;
  }
`;

export const LeftBarStyled = styled.section.attrs<IStyledAttributes>({
  "data-e2e": "left-bar",
})<{ $isAdminApp: boolean; $isNonProduction?: boolean }>`
  grid-area: sidenav;
  position: relative;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  display: flex;
  flex-direction: column;
  /*
     Capping the row above would otherwise just move the loss in here: the nav is taller than a
     short window on its own, and the items that fall off the bottom are the help, the old-site
     link and the avatar menu. Scrolling keeps them reachable, and making this a scroll container
     also sets its automatic minimum size to 0, so it stops forcing the track taller in the first
     place. The scrollbar is hidden because a 62px rail has no room for one; the wheel, the
     keyboard and touch all still work.
  */
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
  box-shadow: 2px 0px 2px rgba(59, 59, 59, 30%);
  background: ${props =>
    props.$isAdminApp ? greyMui._900 : deepPurpleMui._900};
  color: ${deepPurpleMui._900};

  ${props =>
    props.$isNonProduction
      ? `
    background: ${props.$isAdminApp ? rydeColors.nonProductionNavbarGreen : rydeColors.nonProductionNavbarOrangeHover};

    li button:hover,
    li a:hover {
      background-color: ${
        props.$isAdminApp
          ? rydeColors.nonProductionNavbarGreenHover
          : rydeColors.nonProductionNavbarOrangeDark
      };
    }

    & .left-nav-plus-button button .svg_normal {
      color: ${rydeColors.nonProductionNavbarOrange};

      &:hover {
        color: ${rydeColors.nonProductionNavbarOrangeDark};
      }
    }

    & .left-nav-plus-button .centered.drag_over {
      background: ${
        props.$isAdminApp
          ? rydeColors.nonProductionNavbarGreenHover
          : rydeColors.nonProductionNavbarOrangeDark
      };
      box-shadow: 0px 2px 23px rgba(255, 255, 255, 0.5);
    }

    & .MuiBadge-badge {
      box-shadow: 0 0 0 2px ${
        props.$isAdminApp
          ? rydeColors.nonProductionNavbarGreen
          : rydeColors.nonProductionNavbarOrangeHover
      };
    }
  `
      : ""}

  .nav_ul {
    gap: 8px;
  }
  @media (max-height: 760px) {
    .logo_box {
      display: none;
    }
    .nav_ul {
      gap: 4px;
    }
  }
`;

export const LogoBoxStyled = styled.button.attrs<IStyledAttributes>({
  "data-e2e": "logo-box",
})`
  margin-top: 20px;
  margin-bottom: 30px;
  width: 100%;
  height: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${rydeColors.white};
    outline-offset: 2px;
  }
`;

export const LeftBarBottomMenuUl = styled.ul.attrs<IStyledAttributes>({
  "data-e2e": "menu-ul-bottom",
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-inline-start: 0;
  margin-top: auto;
  width: 100%;
  gap: 8px;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  cursor: pointer;
  .svg-go-to-old {
    color: ${rydeColors.white};
    width: 24px;
    height: 24px;
  }
  li.avatar-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-bottom: 17px;
    color: ${rydeColors.white};
    text-decoration: none;
  }
`;

export const LeftBarBottomIconWrap = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "left-bar-bottom-icon-wrap",
})<{ $color: string }>`
  display: inline-flex;
  color: ${props => props.$color};
`;

export const NavButtonTooltipStyled = styled(Tooltip).attrs<
  IStyledAttributes & { $isNonProductionNavbarColor?: boolean }
>(props => ({
  // Tooltip fill is darker than the navbar so it reads as a separate layer on orange/green.
  arrowColor: props.$isNonProductionNavbarColor
    ? rydeColors.nonProductionNavbarOrangeDark
    : deepPurpleMui.A400,
  "data-e2e": "nav-button-tooltip",
}))<{ $isNonProductionNavbarColor?: boolean }>`
  background-color: ${props =>
    props.$isNonProductionNavbarColor
      ? rydeColors.nonProductionNavbarOrangeDark
      : deepPurpleMui.A400};
  color: ${rydeColors.white};
  padding: 0px;

  .idui-popover__content {
    overflow: auto;
    max-height: 100px !important;
    max-width: 200px !important;
    padding: 10px;

    /* webkit-specific */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background-color: ${greyMui._900};
    }
    &::-webkit-scrollbar-thumb {
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      background-color: ${greyMui._400};
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${greyMui._400};
    }
    @supports (-moz-appearance: none) {
      /* mozilla-specific, shouldn't be applied to Chrome */
      scrollbar-width: thin;
    }
  }
`;

export const NavButtonTooltipAdminStyled = styled(Tooltip).attrs<
  IStyledAttributes & { $isNonProductionNavbarColor?: boolean }
>(props => ({
  arrowColor: props.$isNonProductionNavbarColor
    ? rydeColors.nonProductionNavbarGreenHover
    : greyMui._900,
  "data-e2e": "nav-button-tooltip",
}))<{ $isNonProductionNavbarColor?: boolean }>`
  background-color: ${props =>
    props.$isNonProductionNavbarColor
      ? rydeColors.nonProductionNavbarGreenHover
      : greyMui._900};
  color: ${rydeColors.white};
  padding: 10px;
`;
export const LeftBarLiStyled = styled.li.attrs<IStyledAttributes>({
  "data-e2e": "left-bar-li",
})<{ $isAdminApp: boolean }>`
  button,
  a {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    text-align: center;
    display: block;
    text-decoration: none;
    background-color: transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    outline: none;
    &:hover {
      background-color: ${props =>
        props.$isAdminApp ? greyMui._800 : deepPurpleMui.A400};
    }
  }
  a {
    display: flex;
    line-height: 40px;
    justify-content: center;
    align-items: center;
  }

  .svg-go-help-page {
    width: 30px;
    height: 30px;
  }

  .svg-go-help-page,
  .svg-go-feedback-page {
    color: ${rydeColors.white};

    &.active {
      color: ${rydeColors.leftMenuHighlight};
    }
  }
`;

export const LeftNavButtonStyled = styled.li.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "left-nav-button",
  className: "left-nav-plus-button",
})`
  height: 60px;

  button {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    text-align: center;
    display: block;
    text-decoration: none;
    background-color: transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    .svg_normal {
      display: inline-block;
      color: ${rydeColors.leftNavPlusButtonBgColor};
      &:hover {
        color: ${rydeColors.leftNavPlusButtonBgHoverColor};
      }
    }
  }
  .centered {
    display: flex;
    align-items: center;
    justify-content: center;
    &.drag_over {
      background: ${deepPurpleMui.A400};
      box-shadow: 0px 2px 23px rgba(255, 255, 255, 0.5);
    }
  }
`;
